export const colors = {
  brand: {
    laurel: '#177508',
    saffron: '#F6B83F',
    pear: '#CFE531',
    orchid: '#E358C2',
    softLaurel: '#A0EA95',
    darkLaurel: '#0E4805'
  },
  neutral: {
    white: '#FFFFFF',
    offWhite: '#F5F5F5',
    cream: '#FDF7F1',
    lightCharcoal: '#CFCFCF',
    charcoal: '#8E8E8E',
    darkCharcoal: '#4A4A4A',
    black: '#1A1A1A'
  },
  reds: {
    dark: '#C9121A',
    bright: '#EF5258',
    muted: '#F8B1B4'
  },
  yellows: {
    bright: '#F9A825',
    dark: '#F57F17',
  },
  accents: {
    orange: '#FF6514',
    savoy: '#3B60E4'
  }
};

export const spacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
};

interface FilledVariant {
  background: string;
  color: string;
  hoverBackground: string;
  hoverColor: string;
}

interface OutlineVariant extends FilledVariant {
  border: string;
}

export const buttons = {
  sizes: {
    default: {
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      iconSize: '1.25rem',
    },
  },
  variants: {
    filled: {
      primary: {
        background: colors.brand.laurel,
        color: colors.neutral.white,
        hoverBackground: colors.brand.darkLaurel,
        hoverColor: colors.neutral.white,
      } as FilledVariant,
      warning: {
        background: colors.yellows.bright,
        color: colors.neutral.white,
        hoverBackground: colors.yellows.dark,
        hoverColor: colors.neutral.white,
      } as FilledVariant,
      danger: {
        background: colors.reds.bright,
        color: colors.neutral.white,
        hoverBackground: colors.reds.dark,
        hoverColor: colors.neutral.white,
      } as FilledVariant,
    },
    outline: {
      primary: {
        background: 'transparent',
        color: colors.brand.laurel,
        border: `1px solid ${colors.brand.laurel}`,
        hoverBackground: colors.neutral.offWhite,
        hoverColor: colors.brand.darkLaurel,
      } as OutlineVariant,
      warning: {
        background: 'transparent',
        color: colors.yellows.bright,
        border: `1px solid ${colors.yellows.bright}`,
        hoverBackground: colors.neutral.offWhite,
        hoverColor: colors.yellows.dark,
      } as OutlineVariant,
      danger: {
        background: 'transparent',
        color: colors.reds.bright,
        border: `1px solid ${colors.reds.bright}`,
        hoverBackground: colors.neutral.offWhite,
        hoverColor: colors.reds.dark,
      } as OutlineVariant,
    },
  },
};

export const typography = {
  titles: {
    small: {
      fontFamily: '"Alfa Slab One", cursive',
      fontSize: '24px',
      lineHeight: '28px'
    },
    medium: {
      fontFamily: '"Alfa Slab One", cursive',
      fontSize: '32px',
      lineHeight: '36px'
    },
    large: {
      fontFamily: '"Alfa Slab One", cursive',
      fontSize: '52px',
      lineHeight: '56px'
    }
  },
  subtitle: {
    small: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '16px',
      lineHeight: '20px'
    },
    medium: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '18px',
      lineHeight: '24px',
      fontWeight: '400'
    },
    large: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '20px',
      lineHeight: '28px',
      fontWeight: '400'
    }
  },
  paragraph: {
    small: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '16px',
      lineHeight: '20px',
      fontWeight: '400'
    },
    medium: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '18px',
      lineHeight: '24px',
      fontWeight: '400'
    },
    large: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: '400'
    }
  },
  ui: {
    button: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '16px',
      lineHeight: '24px'
    }
  },
  label: {
    small: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '12px',
      lineHeight: '12px'
    },
    medium: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '14px',
      lineHeight: '20px'
    },
    large: {
      fontFamily: 'Gabarito, sans-serif',
      fontSize: '18px',
      lineHeight: '24px'
    }
  }
};

interface InputVariant {
  background: string;
  border: string;
  color: string;
  placeholder: string;
  focusBorder: string;
  disabledBackground: string;
  disabledColor: string;
  errorBorder: string;
  errorColor: string;
}

export const inputs = {
  default: {
    background: colors.neutral.white,
    border: `1px solid ${colors.neutral.lightCharcoal}`,
    color: colors.neutral.darkCharcoal,
    placeholder: colors.neutral.charcoal,
    focusBorder: colors.brand.laurel,
    disabledBackground: colors.neutral.offWhite,
    disabledColor: colors.neutral.charcoal,
    errorBorder: colors.reds.bright,
    errorColor: colors.reds.dark,
  } as InputVariant,
  sizes: {
    default: {
      padding: '0.75rem',
      fontSize: '1rem',
      borderRadius: '4px',
    },
    small: {
      padding: '0.5rem',
      fontSize: '0.875rem',
      borderRadius: '4px',
    },
    large: {
      padding: '1rem',
      fontSize: '1.125rem',
      borderRadius: '4px',
    },
  },
}; 